import RecordingApiService from './RecordingApiService';
import { APP_URL, RECORDING_SERVER_WS, RECORDING_BUCKET_PREFIX } from '@/Utils/vice-constants';
import useSessionStore from '@/Stores/useSessionStore';

class RecordingService {
  constructor() {
    this.ws = null;
    this.recordingServerUrl = RECORDING_SERVER_WS;
    this.activeRecordings = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.apiService = new RecordingApiService();
    this.currentSessionId = null;
    this.currentSessionUid = null;
    this.clinicianId = null;
    this.sessionStore = null;
  }

  async initializeSession(sessionId, sessionUid, clinicianId, enabledDevices = []) {
    this.currentSessionId = sessionId;
    this.currentSessionUid = sessionUid;
    this.clinicianId = clinicianId;
    this.sessionStore = useSessionStore();

    try {
      // Start recording session in Laravel
      await this.apiService.startRecordingSession(sessionId, clinicianId, enabledDevices);
      // Connect to recording server
      await this.connect();

      return true;
    } catch (error) {
      console.error('Failed to initialize recording session:', error);
      throw error;
    }
  }

  async endSession() {
    if (!this.currentSessionId) {
      throw new Error('No active recording session');
    }

    try {
      // Stop all active recordings
      for (const [streamId, recordingInfo] of this.activeRecordings) {
        console.log('Recording session endSession:', streamId, recordingInfo);
        // eslint-disable-next-line no-await-in-loop
        await this.stopRecording(streamId);
      }

      await this.apiService.endRecordingSession(this.currentSessionId);
      this.disconnect();

      this.currentSessionId = null;
      this.clinicianId = null;

      return true;
    } catch (error) {
      console.error('Failed to end recording session:', error);
      throw error;
    }
  }

  connect() {
    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(this.recordingServerUrl);

        this.ws.onopen = () => {
          console.log('Rec: connected');
          this.reconnectAttempts = 0;
          resolve();
        };

        this.ws.onmessage = (event) => {
          this.handleMessage(JSON.parse(event.data));
        };

        this.ws.onclose = () => {
          console.log('Rec: disconnected');
          this.attemptReconnect();
        };

        this.ws.onerror = (error) => {
          console.error('Recording server connection error:', error);
          reject(error);
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  async startRecording(streamId, deviceType, mediaStream) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('Recording server not connected');
    }

    if (!this.currentSessionId) {
      throw new Error('No active recording session');
    }

    const recordingId = `${deviceType}_${Date.now()}`;

    try {
      // Notify backend
      await this.apiService.notifyRecordingStarted(
        recordingId,
        this.currentSessionId,
        this.sessionStore.sessionDetails.university.id,
        streamId,
        deviceType,
        {
          started_by: this.clinicianId,
          stream_quality: this.getStreamQuality(mediaStream),
        },
      );

      // Start recording on server
      this.ws.send(
        JSON.stringify({
          type: 'start_recording',
          sessionId: this.currentSessionId,
          sessionUid: this.currentSessionUid,
          streamId,
          deviceType,
          recordingId,
          universityId: this.sessionStore.sessionDetails.university.id,
          domain: APP_URL,
          bucketPrefix: RECORDING_BUCKET_PREFIX,
        }),
      );

      // Start capturing stream
      const recorder = new MediaRecorder(mediaStream, {
        mimeType: 'video/webm;codecs=vp9,opus',
      });

      const recordingInfo = {
        recorder,
        sessionId: this.currentSessionId,
        sessionUid: this.currentSessionUid,
        streamId,
        deviceType,
        recordingId,
        chunks: [],
        chunkIndex: 0,
      };

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0 && recordingInfo.recordingId) {
          this.sendChunk(recordingInfo, event.data);
        }
      };

      recorder.start(2000); // 2-second chunks
      this.activeRecordings.set(streamId, recordingInfo);

      console.log(`Rec: Started ${recordingId}`);
      return recordingId;
    } catch (error) {
      console.error('Failed to start recording:', error);
      throw error;
    }
  }

  async sendChunk(recordingInfo, chunk) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('Cannot send chunk - recording server not connected');
      return;
    }

    try {
      // Convert blob to base64 and send to recording server
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Data = reader.result.substring(reader.result.lastIndexOf(',') + 1);
        const timestamp = Date.now();

        this.ws.send(
          JSON.stringify({
            type: 'chunk',
            recordingId: recordingInfo.recordingId,
            chunk: base64Data,
            timestamp,
          }),
        );

        // Notify Laravel about chunk (optional, for analytics)
        try {
          await this.apiService.notifyChunkReceived(
            recordingInfo.recordingId,
            (recordingInfo.chunkIndex += 1),
            chunk.size,
            timestamp,
          );
        } catch (error) {
          // Don't fail the recording if chunk notification fails
          console.warn('Failed to notify Laravel about chunk:', error);
        }
      };
      reader.readAsDataURL(chunk);
    } catch (error) {
      console.error('Failed to send chunk:', error);
    }
  }

  async stopRecording(streamId) {
    const recordingInfo = this.activeRecordings.get(streamId);
    if (!recordingInfo) {
      throw new Error('Recording not found');
    }

    try {
      // Stop MediaRecorder
      recordingInfo.recorder.stop();

      // Notify backend
      await this.apiService.notifyRecordingStopped(
        recordingInfo.recordingId,
        this.currentSessionId,
        this.sessionStore.sessionDetails.university.id,
      );

      // fetch processing recording
      await this.updateSnapshotPool(recordingInfo.recordingId)
        .then(() => {
          if (!this.sessionStore.isOpenSidebar) this.sessionStore.toggleSidebar();
          this.sessionStore.sidebarTab = 'sessionTab';
        })
        .catch((error) => {
          console.error('Failed to update snapshot pool:', error);
        });

      // Notify recording server
      if (this.ws && this.ws.readyState === WebSocket.OPEN && recordingInfo.recordingId) {
        this.ws.send(
          JSON.stringify({
            type: 'stop_recording',
            recordingId: recordingInfo.recordingId,
          }),
        );
      }

      this.activeRecordings.delete(streamId);
      console.log(`Rec: Stopped ${recordingInfo.recordingId}`);
      return recordingInfo.recordingId;
    } catch (error) {
      console.error('Failed to stop recording:', error);
      throw error;
    }
  }

  async getSessionRecordings(sessionId = null) {
    const id = sessionId || this.currentSessionId;
    if (!id) {
      throw new Error('No session ID provided');
    }

    try {
      const response = await this.apiService.getSessionRecordings(id);
      return response.recordings;
    } catch (error) {
      console.error('Failed to get session recordings:', error);
      throw error;
    }
  }

  async getRecording(recordingId) {
    try {
      const response = await this.apiService.getRecording(recordingId);
      return response.recording;
    } catch (error) {
      console.error('Failed to get session recordings:', error);
      throw error;
    }
  }

  async updateSnapshotPool(recordingId) {
    try {
      const recording = await this.getRecording(recordingId);
      this.sessionStore.snapshots.push(recording);
    } catch (error) {
      console.error('Failed to update snapshots:', error);
      throw error;
    }
  }

  async getRecordingServerStatus() {
    try {
      const response = await this.apiService.getRecordingServerStatus();
      return response.server_status;
    } catch (error) {
      console.error('Failed to get recording server status:', error);
      throw error;
    }
  }

  getStreamQuality(mediaStream) {
    const videoTrack = mediaStream.getVideoTracks()[0];
    if (videoTrack) {
      const settings = videoTrack.getSettings();
      const quality = {
        width: settings.width,
        height: settings.height,
        frameRate: settings.frameRate,
      };

      this.lastStreamQuality = quality;

      return quality;
    }
    return null;
  }

  handleMessage(message) {
    console.log(`Rec: WS message: "${message.type}" for ${message.recordingId}`);
    this.lastMessage = message;
    switch (message.type) {
      case 'recording_started':
        break;
      case 'recording_stopped':
        break;
      case 'chunk_received':
        break;
      case 'error':
        console.error('Recording server error:', message.message);
        break;
      default:
        console.log('Rec: WS unhandled message:', message.message);
        break;
    }
  }

  attemptReconnect() {
    if (this.reconnectAttempts < this.maxReconnectAttempts && this.currentSessionId) {
      this.reconnectAttempts += 1;
      console.log(
        `Rec: attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`,
      );

      setTimeout(() => {
        this.connect().catch(() => {
          // Reconnection failed, try again
        });
      }, 1000 * this.reconnectAttempts);
    }
  }

  disconnect() {
    if (this.ws) {
      this.ws.close();
    }
  }
}

export default RecordingService;
